import { FetchResult, useMutation } from '@apollo/client';
import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { CommunityContext } from '../../../common_lib_front/communityConfigs/communityContextProvider';
import { ChipsInput } from '../../../common_lib_front/components/ChipsInput/ChipsInput';
import GenericAlert from '../../../common_lib_front/components/genericAlert/genericAlert';
import InputField from '../../../common_lib_front/components/inputField/inputField';
import doValidate from '../../../common_lib_front/components/inviteGuestForm/useRentalValidation';
import PhoneInputWrapper from '../../../common_lib_front/components/phoneInputWrapper/phoneInputWrapper';
import { SpecialInstruction } from '../../../common_lib_front/components/SpecialInstruction';
import useGetMyGuests from '../../../common_lib_front/hooks/useMyGuest';
import useOrderPrice from '../../../common_lib_front/hooks/useOrderPrice';
import useProperty from '../../../common_lib_front/hooks/useProperty';
import PassInfo, {
  newPassInfo,
  PassStatuses,
} from '../../../common_lib_front/types/passInfo';
import VehicleInfo, { newVehicleInfo } from '../../../common_lib_front/types/vehicleInfo';
import { backendClient } from '../../../common_lib_front/utilities/BackendAPI';
import { Auto2fa, auto2faApi } from '../../../components/2faModal/auto2fa';
import { useInviteGuest } from '../../../hooks/useInviteGuest';
import usePassGroups from '../../../utils/usePassGroups';
import useVehicleConfigs, { PassInfoData } from '../../../utils/useVehicleConfigs';
import {
  ADD_PASS,
  ADD_PASS_RES,
  ADD_PASS_VARS,
} from '../../registration/registerVehicle/registerVehicleRequests';
import AddVehicleInfo from './addVehicleInfo';
import { EDIT_PASS, EDIT_PASS_RES } from './editRequests';
import style from './getPasses.module.css';
import {
  EDIT_VEHICLE,
  mapVehicleInfoToEditData,
  mutateWrapperForVehiclePass,
  REGISTER_VEHICLE,
} from './mutateWrapperForVehiclePass';
import PassOptions from './passInfoComponent';
import { GetPassesData, VehiclePass } from './types';
import { PassOptionErrorsType, validateVehicleData } from './validateVehicleData';

const errorMessages = {
  firstName: 'First Name is required.',
  lastName: 'Last Name is required.',
  email: 'Email or Phone number is required.',
  phoneNumber: 'Email or Phone number is required.',
  startDate: 'Check-In & Check-Out dates are required.',
  endDate: 'Check-In & Check-Out dates are required.',
};

type ErrorsType = {
  firstName?: boolean;
  lastName?: boolean;
  email?: boolean;
  phoneNumber?: boolean;
  startDate?: boolean;
  endDate?: boolean;
};

const GROUP_KEY = 'Guest V2';

export default function GetPasses(): ReactElement {
  const history = useHistory();
  const { communityId, featuresConfig } = useContext(CommunityContext);
  const { propertySlug } = useParams<{ propertySlug: string }>();

  const { registrationId } = useParams<{ registrationId: string }>();
  const [shouldPrefillForm, setShouldPrefillForm] = useState(false);
  const location = useLocation();

  const [alert, setAlert] = useState<string>('');
  const [alertColor, setAlertColor] = useState<'red' | 'green'>('red');
  const [smsAlert, setSmsAlert] = useState<boolean>(false);
  const [promotionalAlert, setPromotionalAlert] = useState<boolean>(false);
  const { inviteGuests, data, loading: inviteLoading } = useInviteGuest();
  const [redirectPath, setRedirectPath] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const twoFaRef = useRef<auto2faApi | null>(null);
  const [isVerified, setIsVerified] = useState(twoFaRef.current?.isVerified() || false);

  const { data: myGuestDetails, error: guestDetailsError } = useGetMyGuests({
    registrationIds: registrationId ? [registrationId] : [],
    propertySlug: propertySlug,
  });

  const { data: passBuilderGroups } = usePassGroups();

  const today = new Date().toISOString().split('T')[0];
  const targetDivRef = useRef<HTMLDivElement | null>(null);

  const [getPassesData, setGetPassesData] = useState<GetPassesData>({
    firstName: '',
    email: '',
    phoneNumber: '',
    startDate: '',
    endDate: '',
    lastName: '',
    guestNames: [],
  });

  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });
  const [rentalInfo, setRentalInfo] = useState({
    address: '',
    communityAddressId: '',
    arrivalDate: '',
    departureDate: '',
    guestNames: [] as string[],
  });

  const [passes, setPasses] = useState<PassInfo[]>([]);
  const [passInfoId, setPassInfoId] = useState<PassInfoData[]>([]);
  const [days, setDays] = useState<number>(0);
  const passOptionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const vehiclePassesByType = useMemo(() => {
    const res: Record<
      string,
      (VehicleInfo & { passIndex: number; passStatus: PassStatuses })[]
    > = {};
    passes.forEach((p, passIndex) => {
      const piid = p.passInfoId;
      if (!res[piid]) {
        res[piid] = [];
      }
      res[piid].push({
        ...p.vehicle,
        dontKnowLicense: p.vehicle.isRental,
        passIndex,
        passStatus: p.status,
      });
    });
    return res;
  }, [passes]);
  console.log('Vehicle passes by type: ', vehiclePassesByType);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errors, setErrors] = useState<ErrorsType>({});
  const [passOptionErrors, setPassOptionErrors] = useState<PassOptionErrorsType>({});
  const [addPass] = useMutation<ADD_PASS_RES, ADD_PASS_VARS>(ADD_PASS);

  useEffect(() => {
    if (myGuestDetails?.length) {
      setShouldPrefillForm(true);
      setGuestInfo({
        firstName: myGuestDetails[0].guestInfo.firstName,
        lastName: myGuestDetails[0].guestInfo.lastName,
        email: myGuestDetails[0].guestInfo.email,
        phoneNumber: myGuestDetails[0].guestInfo.phoneNumber,
      });

      setGetPassesData({
        firstName: myGuestDetails[0].guestInfo.firstName,
        email: myGuestDetails[0].guestInfo.email,
        phoneNumber: myGuestDetails[0].guestInfo.phoneNumber,
        startDate: new Date(myGuestDetails[0].communityRental.arrivalDate)
          .toISOString()
          .split('T')[0],
        endDate: new Date(myGuestDetails[0].communityRental.departureDate)
          .toISOString()
          .split('T')[0],
        lastName: myGuestDetails[0].guestInfo.lastName,
        guestNames: myGuestDetails[0]?.communityRental?.guestNames || [],
      });

      setPasses(
        myGuestDetails.flatMap(guest => guest.passes.map(p => newPassInfo(p as any))),
      );
    }
  }, [myGuestDetails]);

  useEffect(() => {
    if (getPassesData.startDate && getPassesData.endDate) {
      const startDate = new Date(getPassesData.startDate);
      const endDate = new Date(getPassesData.endDate);
      const timeDiff = endDate.getTime() - startDate.getTime();
      const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
      setDays(diffDays);
    }
  }, [getPassesData]);

  const {
    data: propertyDetail,
    loading: queryLoading,
    error: propertyError,
  } = useProperty({ propertySlug: propertySlug || '' });
  const { passInfoDatas, vehicleConfig } = useVehicleConfigs();

  // insert charge for full capacity passes
  useEffect(() => {
    setPasses(prevState => {
      let chargeForFullCapacityPassInfos;
      if (!registrationId) {
        chargeForFullCapacityPassInfos = passInfoDatas.filter(
          p => p.priceInfo?.chargeForFullCapacity && p.portal === 'guest',
        );
      }
      const copy = [...prevState];
      chargeForFullCapacityPassInfos?.forEach(cffcPassInfo => {
        if (!prevState.filter(p => p.passInfoId === cffcPassInfo.passInfoId)?.length) {
          copy.push(
            newPassInfo({
              passInfoId: cffcPassInfo.passInfoId,
            }),
          );
        }
      });
      return copy;
    });
  }, [passInfoDatas, setPasses]);

  const handleChange =
    (field: keyof GetPassesData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setGetPassesData({
        ...getPassesData,
        [field]: e.target.value,
      });
    };

  const handlePhoneChange = (phone: string) => {
    setGetPassesData({
      ...getPassesData,
      phoneNumber: phone,
    });
  };

  const handleVehicleChange = (
    passIndex: number,
    field: keyof VehiclePass,
    value: string,
  ) => {
    setPasses(prevState => {
      const res = [...prevState];
      if (['startDate', 'endDate'].includes(field)) {
        res[passIndex] = newPassInfo({
          ...res[passIndex],
          [field]: value,
        });
      } else {
        res[passIndex].vehicle = newVehicleInfo({
          ...res[passIndex].vehicle,
          [field]: value,
        });
      }
      return res;
    });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCheckboxChange = (passIndex: number, val?: boolean) => {
    console.log(passIndex);
    setPasses(prevState => {
      const res = [...prevState];
      const prevPass = res[passIndex];
      const prevVehicle = prevPass?.vehicle;
      res[passIndex] = newPassInfo({
        ...prevPass,
        vehicle: {
          ...prevVehicle,
          isRental: val ?? !prevVehicle.isRental,
        },
      });
      return res;
    });
  };

  const addVehiclePass = (passInfoId: string, passName: string, limit: number | null) => {
    if (limit !== null && limit > 0) {
      const prevLength = vehiclePassesByType[passInfoId]?.length || 0;
      if (prevLength >= limit) {
        setErrorMessage(
          `The maximum limit of ${limit} ${passName} passes has been reached. You cannot add more passes for this type.`,
        );
        return;
      }
    }

    if (getPassesData.startDate && getPassesData.endDate) {
      setPasses(prevState => [
        ...prevState,
        newPassInfo({
          passInfoId,
          startDate: getPassesData.startDate,
          endDate: getPassesData.endDate,
          registrationId: registrationId ?? '', //added for testing
        }),
      ]);
    }

    console.log({ vehiclePassesByType });
  };

  const removeVehiclePass = (passInfoId: string) => {
    setPasses(prevState => {
      const res = [...prevState];

      // cannot use findLastIndex in browser
      let lastMatch = -1;
      for (let i = res.length; i >= 0; --i) {
        const p = res[i];
        if (p?.passInfoId === passInfoId && !['inactive', 'active'].includes(p?.status)) {
          lastMatch = i;
        }
      }
      res.splice(lastMatch, 1);

      return res;
    });
  };

  useEffect(() => {
    const passInfoGroupList = passBuilderGroups?.getPassGroups.data?.find(
      pg => pg.name === GROUP_KEY,
    )?.passInfoList;

    if (passInfoGroupList) {
      const selectedPassInfos = passInfoGroupList
        .map(piid => passInfoDatas.find(pid => pid.passInfoId === piid))
        .filter(pid => !!pid) as PassInfoData[];
      setPassInfoId(selectedPassInfos);
    } else {
      const selectedPassInfos = passInfoDatas.filter(
        passInfoData => passInfoData.portal === 'guest',
      );
      setPassInfoId(selectedPassInfos);
    }
  }, [passInfoDatas, passBuilderGroups]);

  // useEffect(() => {
  //   const filteredPassInfoIds = passInfoId.filter(passInfoData => {
  //     const duration = passInfoData?.durationInfo?.duration1;
  //     const isMatchingPass = passInfoData.portal === 'guest' &&
  //       (typeof duration === 'undefined' || duration === null || duration >= days);
  //       console.log('filteredPassInfoIds', isMatchingPass);
  //     return isMatchingPass;
  //   });
  //   setPassInfoId(filteredPassInfoIds);
  // }, [passInfoDatas, days]);

  const passInfoFilter = (passInfoData: PassInfoData) => {
    // Check for missing fields or incorrect structure
    const maxDuration = passInfoData!.durationInfo!.duration1!;
    if (passInfoData.portal !== 'guest') {
      return false;
    }

    if (!getPassesData.startDate || !getPassesData.endDate) {
      return true;
    }
    return days <= maxDuration;
  };
  const {
    totalPrice: orderPriceTotalPrice,
    fees: orderPriceFees,
    passes: orderPricePasses,
    passesByType: orderPricePassesByType,
    loading: orderPriceLoading,
  } = useOrderPrice(passes, {
    defaultAddressId: propertyDetail?.communityAddressId,
  });

  useEffect(() => {
    setGuestInfo({
      firstName: getPassesData.firstName,
      lastName: getPassesData.lastName,
      email: getPassesData.email,
      phoneNumber: getPassesData.phoneNumber,
    });

    setRentalInfo({
      address: propertyDetail?.address || '',
      communityAddressId: propertyDetail?.communityAddressId || '',
      arrivalDate: getPassesData.startDate,
      departureDate: getPassesData.endDate,
      guestNames: getPassesData.guestNames,
    });
  }, [propertyDetail, getPassesData]);

  const isGuestLink =
    propertyDetail?.guestPay && propertyDetail.guestPropertySlug === propertySlug;
  // const totals = calculateTotals();
  // const { totalCount, totalAmount } = aggregateTotals();
  // Validation logic
  const validateForm = () => {
    const { firstName, lastName, email, phoneNumber, startDate, endDate } = getPassesData;
    const errors: ErrorsType = {};

    if (!firstName) {
      errors.firstName = true;
    }
    if (!lastName) {
      errors.lastName = true;
    }
    if (!email && !phoneNumber) {
      errors.email = true;
      errors.phoneNumber = true;
    }
    if (!startDate) {
      errors.startDate = true;
    }
    if (!endDate) {
      errors.endDate = true;
    }
    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }
    return true;
  };

  // vehicle passes by type are now derrived from passes. no need for this useEffect
  // useEffect(() => {
  //   const updatedPasses = Object.keys(vehiclePassesByType).flatMap(passInfoId => {
  //     return vehiclePassesByType[passInfoId].map(p => ({
  //       passInfoId,
  //       startDate: p.startDate,
  //       endDate: p.endDate,
  //       registrationId: registrationId ?? '', //added for testing
  //     }));
  //   });

  //   setPasses(updatedPasses);
  // }, [vehiclePassesByType]);

  const handleContinue = async (e: any) => {
    console.log('in continue');
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (orderPricePasses?.length === 0) {
      setErrorMessage('Please add at least one pass option b .');
      scrollToTop();
      return;
    }

    setLoading(true);

    try {
      // const updatedPasses = Object.keys(vehiclePassesByType).flatMap(passInfoId => {
      //   return vehiclePassesByType[passInfoId].map(p => ({
      //     passInfoId,
      //     startDate: p.startDate,
      //     endDate: p.endDate,
      //   }));
      // });

      // setPasses(updatedPasses);
      setErrorMessage('');

      const validationResult = validateVehicleData(vehiclePassesByType, vehicleConfig);
      if (validationResult.valid && passes.length > 0) {
        const validate = await doValidate({
          passesRentals: [
            {
              rentalInfo: {
                communityAddressId: rentalInfo.communityAddressId,
                arrivalDate: rentalInfo.arrivalDate,
                departureDate: rentalInfo.departureDate,
              },
              passes: passes.map((pass: any) => ({
                passInfoId: pass.passInfoId,
                endDate: pass.endDate,
                startDate: pass.startDate,
              })),
            },
          ],
        });

        if (
          validate.data.bulkValidate.data &&
          validate?.data?.bulkValidate?.data?.length > 0 &&
          validate.data.bulkValidate.data?.[0] !== ''
        ) {
          setErrorMessage(validate.data.bulkValidate.data?.[0] || '');
        } else {
          const invites = [
            {
              guestInfo: {
                firstName: guestInfo.firstName,
                lastName: guestInfo.lastName,
                email: guestInfo.email,
                phoneNumber: guestInfo.phoneNumber,
              },
              hostWillPay: true,
              passes: passes.map(pass => ({
                passInfoId: pass.passInfoId,
                endDate: pass.endDate,
                startDate: pass.startDate,
              })),
              rentalInfo: {
                communityAddressId: rentalInfo.communityAddressId,
                arrivalDate: rentalInfo.arrivalDate,
                departureDate: rentalInfo.departureDate,
                guestNames: rentalInfo.guestNames,
              },
            },
          ];
          const result = await inviteGuests(invites, propertySlug, true);
          const data = result?.inviteGuests?.data ?? [];
          const addedPasses = data.length > 0 ? data[0].passes ?? [] : [];
          const registrationId =
            data.length > 0 ? data[0].registration?.registrationId ?? '' : '';
          const passesWithId = passes.map((p, idx) =>
            newPassInfo({
              ...p,
              passId: addedPasses[idx].passId,
              vehicle: newVehicleInfo({
                ...p.vehicle,
                destinationAddressId: propertyDetail?.communityAddressId,
                primaryDriverName:
                  p.vehicle?.primaryDriverName ||
                  `${getPassesData.firstName || ''} ${getPassesData.lastName || ''}`,
              }),
            }),
          );
          if (addedPasses.length > 0) {
            //   const formattedPasses = addedPasses.reduce<Record<string, any[]>>(
            //     (acc, curr) => {
            //       const { passInfoId } = curr;
            //       if (passInfoId !== undefined) {
            //         if (!acc[passInfoId]) {
            //           acc[passInfoId] = [];
            //         }
            //         acc[passInfoId].push(curr);
            //       }
            //       return acc;
            //     },
            //     {},
            //   );

            //   const passesWithVehicle = mapPassIdsToVehicles(
            //     formattedPasses,
            //     vehiclePassesByType,
            //     propertySlug,
            //     {
            //       destinationAddressId: propertyDetail?.address || '',
            //       primaryDriverName:
            //         (getPassesData.firstName || '') + ' ' + (getPassesData.lastName || ''),
            //     },
            //   );
            await Promise.all(
              passesWithId
                .filter(p => !!p.passId)
                .map(p =>
                  mutateWrapperForVehiclePass({
                    passId: p.passId,
                    passInfoId: p.passInfoId,
                    propertySlug,
                    vehicleInfo: mapVehicleInfoToEditData(p.vehicle, {
                      destinationAddressId: propertyDetail?.communityAddressId,
                      primaryDriverName: `${getPassesData.firstName || ''} ${
                        getPassesData.lastName || ''
                      }`,
                    }),
                  }),
                ),
            );
            const nextPageURL = `/guest/${propertySlug}/${registrationId}/education`;
            scrollToTop();

            history.replace(nextPageURL);
          } else {
            if (result?.inviteGuests?.error === 'Overlapping Reservations') {
              setErrorMessage(
                'The dates you’ve requested already have too many overlapping reservations and have reached their limit. Please contact your host for further questions.',
              );
            } else {
              setErrorMessage(result?.inviteGuests?.error || '');
            }
          }
        }
      } else {
        setPassOptionErrors(validationResult.errors || {});
      }
    } catch (error) {
      console.log(error);
      setErrorMessage('An error occurred while sending invitations.');
    } finally {
      setLoading(false);
    }
  };
  const handleContinueWithRegistration = async (e: any) => {
    console.log('in continue with reg');
    e.preventDefault();
    // ===== validation =====
    if (!validateForm()) {
      return;
    }
    if (
      !location.pathname.includes('edit-vehicle') && // TODO this check for pathname is garbage
      orderPricePasses?.length === 0
    ) {
      setErrorMessage('Please add at least one pass option c.');
      return;
    }

    setLoading(true);

    try {
      // ===== remap data =====
      // const updatedPasses = Object.keys(vehiclePassesByType).flatMap(passInfoId => {
      //   return vehiclePassesByType[passInfoId].map(p => ({
      //     passInfoId,
      //     startDate: p.startDate,
      //     endDate: p.endDate,
      //     registrationId: registrationId,
      //   }));
      // });
      // setPasses(updatedPasses);
      setErrorMessage('');

      // ===== validation =====
      const validationResult = validateVehicleData(vehiclePassesByType, vehicleConfig);
      if (validationResult.valid && passes.length > 0) {
        let validationError = '';
        if (!location.pathname.includes('edit-vehicle')) {
          const validate = await doValidate({
            passesRentals: [
              {
                rentalInfo: {
                  communityAddressId: rentalInfo.communityAddressId,
                  arrivalDate: rentalInfo.arrivalDate,
                  departureDate: rentalInfo.departureDate,
                },
                passes: passes
                  .filter(p => !p.passId)
                  .map(pass => ({
                    passInfoId: pass.passInfoId,
                    endDate: pass.endDate,
                    startDate: pass.startDate,
                  })),
              },
            ],
          });
          console.log({ validatefromregistrationContinue: validate });
          validationError = validate.data.bulkValidate.data?.[0] || '';
        }

        if (validationError) {
          setErrorMessage(validationError);
        } else {
          const passesToCreate = passes.filter(p => !p?.passId);
          const passesToEdit = passes.filter(p => !!p?.passId);
          const createPassResults = (await Promise.all(
            // create new passes
            passesToCreate.map(pass =>
              addPass({
                variables: {
                  passInfoId: pass.passInfoId,
                  registrationId,
                },
              }),
            ),
          )) as FetchResult<ADD_PASS_RES>[];

          const editPassResults = (await Promise.all(
            // edit passes
            passesToEdit.map(pass =>
              backendClient.mutate({
                mutation: EDIT_PASS,
                variables: {
                  passId: pass.passId,
                  newInfo: {
                    passInfoId: pass.passInfoId,
                    startDate: pass.startDate,
                    endDate: pass.endDate,
                  },
                },
              }),
            ),
          )) as FetchResult<EDIT_PASS_RES>[];

          // check for errors
          const createPassError = createPassResults.find(
            res => res.data?.addPassToInvite?.error,
          )?.data?.addPassToInvite?.error;
          const editPassError = editPassResults.find(res => res.data?.editPass?.error)
            ?.data?.editPass?.error;
          if (createPassError || editPassError) {
            setErrorMessage(
              createPassError ||
                editPassError ||
                'Something went wrong while submitting passes',
            );
          } else {
            const addedPasses = createPassResults.map(
              result => result?.data?.addPassToInvite?.data?.[0]?.passes?.[0],
            );
            // map passInfoId => pass[]
            // const passesWithVehicle = mapPassIdsToVehicles(
            //   formattedPasses,
            //   vehiclePassesByType,
            //   propertySlug,
            //   {
            //     destinationAddressId: propertyDetail?.address || '',
            //     primaryDriverName:
            //       (getPassesData.firstName || '') +
            //       ' ' +
            //       (getPassesData.lastName || ''),
            //   },
            // );
            // create new vehicles
            const vehiclesToCreate = passesToCreate.map((p, idx) =>
              newPassInfo({
                ...p,
                passId: addedPasses[idx]?.passId,
              }),
            );
            const vehiclesToEdit: PassInfo[] = [];
            passesToEdit.forEach(p => {
              if (!p.vehicle.vehicleId) {
                vehiclesToCreate.push(p);
              } else {
                vehiclesToEdit.push(p);
              }
            });
            const pdnDefault = `${getPassesData.firstName || ''} ${
              getPassesData.lastName || ''
            }`;
            await Promise.all(
              vehiclesToCreate.map(p =>
                backendClient.mutate({
                  mutation: REGISTER_VEHICLE,
                  variables: {
                    passId: p.passId,
                    passInfoId: p.passInfoId,
                    propertySlug,
                    vehicleInfo: mapVehicleInfoToEditData(p.vehicle, {
                      primaryDriverName: pdnDefault,
                    }),
                  },
                }),
              ),
            );
            await Promise.all(
              vehiclesToEdit.map(p =>
                backendClient.mutate({
                  mutation: EDIT_VEHICLE,
                  variables: {
                    vehicleId: p.vehicle.vehicleId,
                    passInfoId: p.passInfoId,
                    newVehicleInfo: mapVehicleInfoToEditData(p.vehicle, {
                      primaryDriverName: pdnDefault,
                    }),
                  },
                }),
              ),
            );
            const nextPageURL = `/guest/${propertySlug}/${registrationId}/education`;
            scrollToTop();

            history.replace(nextPageURL, {
              overwriteAllowSkip: location.pathname.includes('edit-vehicle') || undefined,
            });
          }
        }
      } else {
        setPassOptionErrors(validationResult.errors || {});
      }
    } catch (error) {
      console.log(error);
      setErrorMessage('An error occurred while sending invitations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (errorMessage) {
      if (targetDivRef.current) {
        const elementPosition = targetDivRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - 200; // Adjust scroll position by -100px

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
        // targetDivRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [errorMessage]);

  const hiddenAlert = () => {
    setTimeout(() => {
      setAlert('');
    }, 4000);
  };

  useEffect(() => {
    const errorEntries = Object.entries(passOptionErrors).flatMap(([passId, errorsObj]) =>
      Object.entries(errorsObj)
        .filter(([, errorsArray]) => errorsArray.length > 0)
        .map(([index, errorsArray]) => ({
          passId,
          index: Number(index),
          errors: errorsArray,
        })),
    );

    errorEntries.sort((a, b) => a.index - b.index);

    if (errorEntries.length > 0) {
      const firstError = errorEntries[0];
      if (firstError.errors && firstError.errors.length > 0) {
        const errorElement = passOptionRefs.current[firstError.index];
        if (errorElement) {
          const offsetPosition =
            errorElement.getBoundingClientRect().top + window.scrollY - 150;
          setAlert('Please fill out the required information');
          setAlertColor('red');
          hiddenAlert();
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }
    }
  }, [passOptionErrors]);

  useEffect(() => {
    if (queryLoading || !propertySlug || !passInfoDatas) {
      setLoading(true);
      return;
    }
    if (propertyError) {
      setRedirectPath(`/guest/${propertySlug}/link-disabled`);
      setLoading(false);
    } else {
      if (propertyDetail) {
        if (
          (propertyDetail?.hostPropertySlug == propertySlug &&
            propertyDetail?.hostPay === false) ||
          (propertyDetail?.guestPropertySlug == propertySlug &&
            propertyDetail?.guestPay === false)
        ) {
          setRedirectPath(`/guest/${propertySlug}/link-disabled`);
        }
      }
      setLoading(false);
    }
  }, [propertyDetail, propertySlug, queryLoading, passInfoDatas, propertyError]);

  if (redirectPath) {
    return <Redirect to={redirectPath} />;
  }

  return (
    <div className={style.container}>
      <div className={`${style.box} white noShadow`}>
        <form
          className={`${style.passForm}`}
          onSubmit={shouldPrefillForm ? handleContinueWithRegistration : handleContinue}
          onKeyDown={ev => {
            if (
              ev.key === 'Enter' &&
              ev.target instanceof HTMLInputElement &&
              ev.target.id === 'guest-names-inpt'
            ) {
              ev.preventDefault();
            }
          }}
        >
          <h1 className={`${style.subHeaderTitle} textColorBlack`}>
            {location.pathname.includes('edit-vehicle')
              ? 'Complete your access passes'
              : 'Get your access passes'}
          </h1>
          <SpecialInstruction
            pathName="guest.v2.select-passes"
            sx={{
              mt: 2,
            }}
          />
          <div className={`${style.mainBox}`}>
            <div className={`${style.innerBox}`}>
              <div className={`${style.propertyDetail}`}>
                <div>
                  <h3 className={`${style.propertyHeading}`}>Property Details</h3>
                </div>
                <div className={`${style.propertyAddress}`}>
                  {propertyDetail?.address}, {propertyDetail?.city},{' '}
                  {propertyDetail?.state} {propertyDetail?.zipCode}
                </div>
              </div>
            </div>

            <div className={style.alert}>
              <div className={style.alertBox}>
                <GenericAlert color={alertColor} title={alert} hidden={!alert} />
              </div>
            </div>

            {errorMessage != '' && (
              <div ref={targetDivRef} className={style.errorContainer}>
                <p className={style.errorMessage}>{errorMessage}</p>
              </div>
            )}

            <div className={style.guestDetailBox}>
              <div className={style.row}>
                <div className={style.box__inputField}>
                  <label htmlFor="firstName-inpt" className={style.label}>
                    First Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className={style.inputBox}>
                    <InputField
                      required
                      smallSize
                      htmlFor="firstName-inpt"
                      inputType="text"
                      inputValue={getPassesData.firstName}
                      highlightOnFocus
                      changeHandler={handleChange('firstName')}
                      inputPlaceholder="Add your first name"
                      disabled={shouldPrefillForm}
                    />
                    {errors.firstName ? (
                      <span style={{ color: 'red' }}>{errorMessages.firstName}</span>
                    ) : null}
                  </div>
                </div>
                <div className={style.box__inputField}>
                  <label htmlFor="lastName-inpt" className={style.label}>
                    Last Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className={style.inputBox}>
                    <InputField
                      required
                      smallSize
                      htmlFor="lastName-inpt"
                      inputType="text"
                      inputValue={getPassesData.lastName}
                      highlightOnFocus
                      changeHandler={handleChange('lastName')}
                      inputPlaceholder="Add your last name"
                      disabled={shouldPrefillForm}
                    />
                    {errors.lastName ? (
                      <span style={{ color: 'red' }}>{errorMessages.lastName}</span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className={style.row}>
                <div className={style.box__inputField}>
                  <label htmlFor="email-inpt" className={style.label}>
                    Email Address <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className={style.inputBox}>
                    <InputField
                      required
                      smallSize
                      htmlFor="email-inpt"
                      inputType="email"
                      inputValue={getPassesData.email}
                      highlightOnFocus
                      changeHandler={handleChange('email')}
                      inputPlaceholder="Enter your email address"
                      disabled={shouldPrefillForm}
                    />
                    {errors.email ? (
                      <span style={{ color: 'red' }}>{errorMessages.email}</span>
                    ) : null}
                  </div>
                </div>
                <div className={style.box__inputField}>
                  <label htmlFor="phone-inpt" className={style.label}>
                    Phone Number <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className={style.inputBox}>
                    <PhoneInputWrapper
                      value={getPassesData.phoneNumber}
                      changeHandler={handlePhoneChange}
                      smallStandardInput
                      required={true}
                      placeHolder="Enter your phone number"
                      disabled={shouldPrefillForm}
                    />
                    {errors.phoneNumber ? (
                      <span style={{ color: 'red' }}>{errorMessages.phoneNumber}</span>
                    ) : null}
                  </div>
                  <span style={{ color: 'rgba(0,0,0,0.8)' }}>
                    By providing your phone number, you agree to receive text messages
                    from Symliv. Message and data rates may apply.
                  </span>
                </div>
              </div>
              <div className={style.row}>
                <div className={style.box__inputField}>
                  <label htmlFor="check-in-inpt" className={style.label}>
                    Check-in Date <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className={style.inputBox}>
                    <InputField
                      required
                      smallSize
                      htmlFor="startDate-inpt"
                      inputType="date"
                      inputValue={getPassesData.startDate}
                      highlightOnFocus
                      changeHandler={handleChange('startDate')}
                      inputPlaceholder="Choose the date"
                      min={today}
                      disabled={shouldPrefillForm}
                    />
                    {errors.startDate ? (
                      <span style={{ color: 'red' }}>{errorMessages.startDate}</span>
                    ) : null}
                  </div>
                </div>
                <div className={style.box__inputField}>
                  <label htmlFor="check-out-inpt" className={style.label}>
                    Check-out Date <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className={style.inputBox}>
                    <InputField
                      required
                      smallSize
                      htmlFor="endDate-inpt"
                      inputType="date"
                      inputValue={getPassesData.endDate}
                      highlightOnFocus
                      changeHandler={handleChange('endDate')}
                      inputPlaceholder="Choose the date"
                      min={getPassesData.startDate}
                      disabled={shouldPrefillForm}
                    />
                    {errors.endDate ? (
                      <span style={{ color: 'red' }}>{errorMessages.endDate}</span>
                    ) : null}
                  </div>
                </div>
              </div>
              {Boolean(featuresConfig?.guest?.guestNames) && (
                <div className={style.row}>
                  <div className={style.box__inputField}>
                    <label htmlFor="guest-names-inpt" className={style.label}>
                      Guest Names <span style={{ color: 'red' }}>*</span>
                    </label>
                    <ChipsInput
                      value={getPassesData.guestNames}
                      containerProps={{
                        className: style.inputBox,
                      }}
                      inputProps={{
                        id: 'guest-names-inpt',
                        placeholder: getPassesData.guestNames.length
                          ? ''
                          : 'Add names for all of your guests (Hit enter after each)',
                      }}
                      setValue={val =>
                        setGetPassesData(prev => ({
                          ...prev,
                          guestNames: val,
                        }))
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            {isVerified && <h3 className={style.passOptLbl}>Pass Options:</h3>}
            {location.pathname.includes('edit-vehicle') ? (
              <>
                {isVerified && (
                  <AddVehicleInfo
                    passInfos={passInfoId.filter(passInfoFilter)}
                    // passes={myGuestDetails?.[0]?.passes || []}
                    passes={passes}
                    // paidPassIds={
                    //   myGuestDetails?.[0]?.passes
                    //     .filter(p => p.paid === 'paid')
                    //     .map(p => p.passInfoId) || []
                    // }
                    days={days}
                    startDate={getPassesData.startDate}
                    endDate={getPassesData.endDate}
                    isGuestLink={isGuestLink}
                    addVehiclePass={addVehiclePass}
                    removeVehiclePass={removeVehiclePass}
                    handleVehicleChange={handleVehicleChange}
                    handleCheckboxChange={handleCheckboxChange}
                    errors={passOptionErrors}
                    priceSums={
                      orderPriceLoading
                        ? null
                        : Object.fromEntries(
                            Object.entries(orderPricePassesByType || {}).map(
                              ([piid, prices]) => [
                                piid,
                                prices.reduce((sum, val) => sum + (val.price || 0), 0),
                              ],
                            ),
                          )
                    }
                  />
                )}
                <div className={`${style.innerBox} ${style.buttonBox}`}>
                  <div className={style.buttonContainer}>
                    {isVerified ? (
                      !loading ? (
                        <button
                          className={style.button}
                          onClick={() => {
                            console.log('Save Vehicle object');
                          }}
                          type="submit"
                        >
                          Save
                        </button>
                      ) : (
                        <button className={style.button} type="button">
                          Loading...
                        </button>
                      )
                    ) : (
                      <button
                        className={style.button}
                        type="button"
                        onClick={() => {
                          if (twoFaRef.current) {
                            twoFaRef.current.setUserData({
                              firstName: getPassesData.firstName || '',
                              lastName: getPassesData.lastName || '',
                              phone: getPassesData.phoneNumber || '',
                              email: getPassesData.email || '',
                              other: {
                                startDate: getPassesData.startDate || '',
                                endDate: getPassesData.endDate || '',
                              },
                            });
                            twoFaRef.current.openModal();
                          }
                        }}
                      >
                        Get Started
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {isVerified && (
                  <PassOptions
                    refArray={passOptionRefs}
                    isEditMode={false}
                    passInfos={passInfoId.filter(passInfoFilter)}
                    vehiclePassesByType={vehiclePassesByType}
                    orderPricePasses={orderPricePasses}
                    paidPassInfoIds={
                      myGuestDetails?.[0]?.passes
                        .filter(
                          p =>
                            p.paid === 'paid' &&
                            p.passInfo?.priceInfo?.chargeForFullCapacity,
                        )
                        .map(p => p.passInfoId) || []
                    }
                    days={days}
                    startDate={getPassesData.startDate}
                    endDate={getPassesData.endDate}
                    isGuestLink={isGuestLink}
                    addVehiclePass={addVehiclePass}
                    removeVehiclePass={removeVehiclePass}
                    handleVehicleChange={handleVehicleChange}
                    handleCheckboxChange={handleCheckboxChange}
                    errors={passOptionErrors}
                    priceSums={
                      orderPriceLoading
                        ? null
                        : Object.fromEntries(
                            Object.entries(orderPricePassesByType || {}).map(
                              ([piid, prices]) => [
                                piid,
                                prices.reduce((sum, val) => sum + (val.price || 0), 0),
                              ],
                            ),
                          )
                    }
                  />
                )}
                {isVerified && (
                  <div className={style.innerBox}>
                    <div style={{ margin: '20px 10px 10px 0px' }}>
                      <h3>
                        Subtotal ({passes?.length}{' '}
                        {(passes?.length || 0) > 1 ? 'passes' : 'pass'})
                      </h3>
                    </div>
                    <div className={style.totals}>
                      {Object.entries(vehiclePassesByType || {}).map(
                        ([piid, vehiclePasses]) =>
                          vehiclePasses.length === 0 ? (
                            <></>
                          ) : (
                            <>
                              <div key={piid} className={style.totalBox}>
                                <div
                                  className={style.row}
                                  style={{ margin: '10px 10px 10px 0px' }}
                                >
                                  <p>
                                    ({vehiclePasses.length ?? 0}){' '}
                                    {passInfoDatas.find(p => p.passInfoId === piid)
                                      ?.name || 'Access Pass'}
                                  </p>
                                  {isGuestLink && (
                                    <p>
                                      $
                                      {orderPricePassesByType?.[piid]
                                        ?.reduce<number>(
                                          (sum, val) => sum + (val.price ?? 0),
                                          0,
                                        )
                                        ?.toFixed(2) || '0.00'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </>
                          ),
                      )}
                      {orderPriceFees?.map(fee => (
                        <>
                          <div className={style.totalBox}>
                            <div
                              className={style.row}
                              style={{ margin: '10px 10px 10px 0px' }}
                            >
                              <p>{fee.name || 'Fee'}</p>
                              <p>${fee.amount?.toFixed(2) || '0.00'}</p>
                            </div>
                          </div>
                        </>
                      ))}
                      {isGuestLink && (
                        <>
                          <hr className={style.horizontalLine} />
                          <div
                            className={style.row}
                            style={{ margin: '10px 10px 10px 0px' }}
                          >
                            <h4>Total</h4>
                            <h4>
                              {(orderPricePasses?.length || 0) > 0
                                ? `$${orderPriceTotalPrice.toFixed(2)}`
                                : '$0'}
                            </h4>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {isVerified && (
                  <>
                    <div
                      className={style.checkboxContainer}
                      style={{ marginTop: '50px' }}
                    >
                      <input
                        type="checkbox"
                        checked={smsAlert}
                        onChange={() => setSmsAlert(!smsAlert)}
                      />
                      <span className={style.checkboxText}>
                        Opt in to receive important SMS notifications about emergencies,
                        safety updates, and critical community alerts. We&apos;ll keep you
                        informed during any urgent situations.
                      </span>
                    </div>
                  </>
                )}
                <div className={`${style.innerBox} ${style.buttonBox}`}>
                  <div className={style.buttonContainer}>
                    {isVerified ? (
                      !loading ? (
                        <button
                          className={style.button}
                          onClick={
                            shouldPrefillForm
                              ? handleContinueWithRegistration
                              : handleContinue
                          }
                          type="submit"
                        >
                          Continue
                        </button>
                      ) : (
                        <button className={style.button} type="button">
                          Loading...
                        </button>
                      )
                    ) : (
                      <button
                        className={style.button}
                        type="button"
                        onClick={() => {
                          if (twoFaRef.current) {
                            twoFaRef.current.setUserData({
                              firstName: getPassesData.firstName || '',
                              lastName: getPassesData.lastName || '',
                              phone: getPassesData.phoneNumber || '',
                              email: getPassesData.email || '',
                            });
                            twoFaRef.current.openModal();
                          }
                        }}
                      >
                        Continue
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </form>
      </div>
      <Auto2fa
        apiRef={twoFaRef}
        onSuccess={() => {
          setIsVerified(true);
          const userData = twoFaRef.current?.getUserData();
          if (userData) {
            setGetPassesData(prev => ({
              ...prev,
              firstName: prev.firstName || userData.firstName || '',
              lastName: prev.lastName || userData.lastName || '',
              phoneNumber: prev.phoneNumber || userData.phone || '',
              email: prev.email || userData.email || '',
              startDate: prev.startDate || userData.other?.startDate || '',
              endDate: prev.endDate || userData.other?.endDate || '',
            }));
          }
        }}
        onRehydrate={() => {
          setIsVerified(true);
          const userData = twoFaRef.current?.getUserData();
          if (userData) {
            setGetPassesData(prev => ({
              ...prev,
              firstName: prev.firstName || userData.firstName || '',
              lastName: prev.lastName || userData.lastName || '',
              phoneNumber: prev.phoneNumber || userData.phone || '',
              startDate: prev.startDate || userData.other?.startDate || '',
              endDate: prev.endDate || userData.other?.endDate || '',
            }));
          }
        }}
      />
    </div>
  );
}
